import { useEffect, useRef, useState } from 'react';
import { IconRefreshCw } from './Icons';

// Custom pull-to-refresh: the browser's own gesture is disabled globally
// (overscroll-behavior-y: none in index.css) because it shifted the whole
// page down and revealed blank space above it. This re-implements just the
// "pull down at the top of the page to reload" affordance without ever
// moving page content -- only a small fixed indicator fades/grows in to
// show progress, the page itself never shifts.
const PULL_THRESHOLD = 70;
const MAX_PULL = 100;

export default function PullToRefresh() {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(null);
  const pullRef = useRef(0);
  const refreshingRef = useRef(false);

  useEffect(() => {
    const reset = () => { startY.current = null; pullRef.current = 0; setPull(0); };

    const onTouchStart = (e) => {
      if (refreshingRef.current || window.scrollY > 0) { startY.current = null; return; }
      startY.current = e.touches[0].clientY;
    };
    const onTouchMove = (e) => {
      if (startY.current == null || refreshingRef.current) return;
      const delta = e.touches[0].clientY - startY.current;
      if (delta <= 0) return;
      if (window.scrollY > 0) { reset(); return; }
      // We're driving this gesture ourselves -- stop the browser from also
      // trying to scroll/rubber-band while the finger is down.
      e.preventDefault();
      const next = Math.min(delta, MAX_PULL);
      pullRef.current = next;
      setPull(next);
    };
    const onTouchEnd = () => {
      if (startY.current == null) return;
      startY.current = null;
      if (pullRef.current >= PULL_THRESHOLD) {
        refreshingRef.current = true;
        setRefreshing(true);
        window.location.reload();
      } else {
        pullRef.current = 0;
        setPull(0);
      }
    };

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd, { passive: true });
    document.addEventListener('touchcancel', onTouchEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
      document.removeEventListener('touchcancel', onTouchEnd);
    };
  }, []);

  const progress = Math.min(pull / PULL_THRESHOLD, 1);
  if (pull <= 8 && !refreshing) return null;

  return (
    <div
      className={`ptr-indicator${refreshing ? ' refreshing' : ''}`}
      style={!refreshing ? { opacity: progress, transform: `translateX(-50%) scale(${0.6 + progress * 0.4})` } : undefined}
      aria-hidden="true"
    >
      <IconRefreshCw size={18} />
    </div>
  );
}
